import {CircularProgress, IconButton, TableCell, TableRow} from '@material-ui/core';
import React from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  TABLE_ROW_SIZE,
  THUMBNAIL_SIZE_LIST,
} from '../../../base-components/StudioDataset/const';
import cn from 'classnames';
import {
  FileSizeCell,
  ImageDimensionCell,
  LastEditedCell,
  ListViewerProps,
  SelectorCell,
  FilenameCell,
  getRenderer,
  ROW_SIZE,
} from '../../../base-components/StudioDataset/FileViewer/ListViewer';
import StudioVirtualizedTable from '../../../base-components/StudioVirtualizedTable/StudioVirtualizedTable';
import {
  StudioVirtualizedTableCell,
  TableHeaderCell,
  TableHeadersProps,
  useStudioVirtualizedTableRow,
} from '../../../base-components/StudioVirtualizedTable/TableComponents';
import {SelectButton} from '../../../base-components/StudioDataset/FileViewer/SelectButton';
import {RendererClassificationProps} from '../../../base-components/StudioDataset/Renderer';
import VideocamIcon from '@material-ui/icons/Videocam';
import ArchiveIcon from '@material-ui/icons/Archive';
import FileIcon from '@material-ui/icons/InsertDriveFile';
import {ImageItem} from '../../../base-components/StudioDataset/FileViewer/ImageItem';
import {GridRendererProps} from './DataReview';
import CancelIcon from '@material-ui/icons/Cancel';

export type ListViewerCustomProps = {
  enableSelection?: boolean;
  onDelete?: (itemId: string) => void;
};

export const ListViewer = ({
  columns: DEFAULT_COLUMNS,
  items = [],
  rowSize,
  imageRenderer = () => <></>,
  onSelect = () => {},
  onClick = () => {},
  onDelete,
  selections = [],
  totalRowCount = 0,
  onSelectAll = () => {},
  enableSelection = true,
  busyItems = [],
  ...rest
}: ListViewerProps & ListViewerCustomProps) => {
  //remove the selector column since we will add it manually with custom behaviors
  const mainHeadersColumns = DEFAULT_COLUMNS.filter(
    el => el.id !== 'label' && el.id !== 'select'
  );

  return (
    <StudioVirtualizedTable
      columns={mainHeadersColumns}
      data={items}
      rowSize={TABLE_ROW_SIZE}
      className="list-view"
      {...rest}
      headerRenderer={({classes, columns, ...rest}: TableHeadersProps) => {
        //  Custom header | Adding Select All functionality
        const numSelected = selections.length;
        // Still unsure which total we use to dtermine the selectAll state tocal vs data row count
        const rowCount = items.length;
        return (
          <TableRow className={cn(classes.row, classes.headerRow)}>
            {enableSelection && (
              <TableCell className="select_cell">
                <SelectButton
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  // Pass empty since list select all has different handling
                  onChange={(e, val) => onSelectAll(val, [])}
                  inputProps={{'aria-label': 'select all items'}}
                />
              </TableCell>
            )}
            {mainHeadersColumns.map((column, colIndex) => {
              return (
                <TableHeaderCell
                  key={`header-${column.id}`}
                  column={column}
                  colIndex={colIndex}
                  classes={classes}
                  columns={columns}
                  {...rest}
                />
              );
            })}
          </TableRow>
        );
      }}
    >
      {/* Row */}
      {enableSelection && <SelectorCell onSelect={onSelect} selections={selections} />}
      <ImageCell busyItems={busyItems} onClick={onClick} />
      <FilenameCell />
      <LastEditedCell />
      <ImageDimensionCell />
      <FileSizeCell field="size" />
      {onDelete && <DeleteCell onDelete={onDelete} />}
    </StudioVirtualizedTable>
  );
};

export const DeleteCell = ({
  onDelete = () => {},
}: Pick<ListViewerCustomProps, 'onDelete'>) => {
  const {item} = useStudioVirtualizedTableRow();

  return (
    <StudioVirtualizedTableCell className="delete-item">
      <IconButton
        size="small"
        color="secondary"
        aria-label="delete"
        onClick={() => onDelete(item.id)}
        data-testid="file-delete-button"
      >
        <DeleteIcon />
      </IconButton>
    </StudioVirtualizedTableCell>
  );
};

export const ImageCell = ({
  onClick = () => {},
  busyItems,
}: Pick<ListViewerProps, 'selections' | 'onClick' | 'busyItems'>) => {
  const {item: rawItem} = useStudioVirtualizedTableRow();
  const isBusy = busyItems?.includes(rawItem?.id);

  let url = rawItem.file || rawItem.url;

  if (typeof url === 'string') {
    const urlObject = new URL(url, window.location.origin);

    if (!urlObject.searchParams.has('previewDimensions')) {
      urlObject.searchParams.set(
        'previewDimensions',
        `${THUMBNAIL_SIZE_LIST}x${THUMBNAIL_SIZE_LIST}`
      );
    }

    url = urlObject.toString();
  }

  const item = {
    ...rawItem,
    file: url,
  };

  return (
    <StudioVirtualizedTableCell
      style={{flexBasis: 55, height: ROW_SIZE, margin: 0, padding: 0}}
      className="image_cell"
      onClick={() => onClick(item)}
    >
      {getRenderer(
        item,
        <BusyOverlay isBusy={isBusy} item={item} onClick={onClick}>
          <UploadImageRenderer item={item} isBusy={isBusy} url={item?.url} />
        </BusyOverlay>
      )}
    </StudioVirtualizedTableCell>
  );
};

export const UploadImageRenderer = ({
  item,
  url: file,
  isBusy,
}: Pick<RendererClassificationProps, 'url' | 'item' | 'isBusy'>) => {
  if (file instanceof File || item.type !== 'image') {
    const style = {
      width: `${THUMBNAIL_SIZE_LIST}px`,
      height: `${THUMBNAIL_SIZE_LIST}px`,
      opacity: isBusy ? 0.1 : 1,
    };
    switch (item.type) {
      case 'archive':
        return <ArchiveIcon color="primary" style={style} />;
      case 'video':
        return <VideocamIcon color="primary" style={style} />;
      case 'image':
        return (
          <ImageItem
            url={file}
            width={THUMBNAIL_SIZE_LIST}
            height={THUMBNAIL_SIZE_LIST}
          />
        );
      default:
        <FileIcon color="primary" style={style} />;
    }
  } else {
    return (
      <ImageItem
        url={file}
        shouldIncludeToken={false}
        width={THUMBNAIL_SIZE_LIST}
        height={THUMBNAIL_SIZE_LIST}
      />
    );
  }
  return <></>;
};

export const BusyOverlay = ({
  isBusy,
  progress = 0,
  hideCloseButton = false,
  children,
  onClick = () => {},
  item,
}: {children?: React.ReactNode; hideCloseButton?: boolean} & Pick<
  GridRendererProps,
  'progress' | 'isBusy' | 'item' | 'onClick'
>) => {
  return isBusy ? (
    <div className="grid-renderer-overlay">
      <div className="grid-renderer-overlay__item-container">
        <CircularProgress
          color="primary"
          variant={progress > 0 ? 'determinate' : 'indeterminate'}
          value={progress}
        />
      </div>
      <div className="grid-renderer-overlay__item-container">{children}</div>
      {hideCloseButton ? null : (
        <div className="grid-renderer-overlay__item-container">
          <IconButton
            aria-label="cancel"
            size="medium"
            onClick={ev => {
              ev.stopPropagation();
              onClick(item);
            }}
          >
            <CancelIcon color="secondary" />
          </IconButton>
        </div>
      )}
    </div>
  ) : (
    <>{children}</>
  );
};
